import { useCallback, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useSearchParams } from 'react-router-dom'
import { AnimatePresence } from 'framer-motion'
import { storeConfig } from '../../../../store.config'
import { Alert } from '../../components/atoms/Alert'
import { Spinner } from '../../components/atoms/Spinner'
import { Paginator } from '../../components/molecules/Paginator'
import { StorefrontBackdrop, StorefrontNav } from '../../components/storefront/chrome'
import { Reveal, Stagger, StaggerItem } from '../../motion'
import { useProfileContext } from '../../../context/ProfileContext'
import {
  useBuildProducts,
  type BuildProductsFilter
} from '../../../viewmodels/useBuildProducts'
import { BuildProductCard } from './BuildProductCard'
import { BuildFilterDrawer } from './filters/BuildFilterDrawer'

/**
 * `/build` — PUBLIC product list mirroring /shop, seeded as the entry
 * point for the future "select products → publish sub-domain store"
 * flow. Same data as the storefront, separate URL + viewmodels +
 * handlers so the two can diverge without coupling.
 *
 * Filter state lives in URL search params (same shape as /shop).
 */
export function BuildListPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams()
  const profile = useProfileContext()
  const [filterOpen, setFilterOpen] = useState(false)

  const filter: BuildProductsFilter = useMemo(() => {
    const q = searchParams.get('q') ?? ''
    const categoryIds = (searchParams.get('categoryIds') ?? '')
      .split(',').map((s) => s.trim()).filter((s) => s.length > 0)
    const min = searchParams.get('priceMinPaisa')
    const max = searchParams.get('priceMaxPaisa')
    const attributeValues: Record<string, ReadonlyArray<string>> = {}
    for (const [k, v] of searchParams.entries()) {
      if (!k.startsWith('attr_')) continue
      const attrId = k.slice('attr_'.length)
      const vals = v.split(',').map((s) => s.trim()).filter((s) => s.length > 0)
      if (vals.length > 0) attributeValues[attrId] = vals
    }
    return {
      q,
      categoryIds,
      priceMinPaisa: min === null || min === '' ? null : Number(min),
      priceMaxPaisa: max === null || max === '' ? null : Number(max),
      attributeValues
    }
  }, [searchParams])

  const writeFilter = useCallback(
    (next: BuildProductsFilter): void => {
      const params = new URLSearchParams()
      if (next.q.trim().length > 0) params.set('q', next.q.trim())
      if (next.categoryIds.length > 0) params.set('categoryIds', next.categoryIds.join(','))
      if (next.priceMinPaisa !== null) params.set('priceMinPaisa', String(next.priceMinPaisa))
      if (next.priceMaxPaisa !== null) params.set('priceMaxPaisa', String(next.priceMaxPaisa))
      for (const [attrId, vals] of Object.entries(next.attributeValues)) {
        if (vals.length === 0) continue
        params.set(`attr_${attrId}`, vals.join(','))
      }
      setSearchParams(params, { replace: true })
    },
    [setSearchParams]
  )

  const [draftQ, setDraftQ] = useState<string>(filter.q)
  useEffect(() => { setDraftQ(filter.q) }, [filter.q])
  useEffect(() => {
    if (draftQ === filter.q) return
    const id = window.setTimeout(() => writeFilter({ ...filter, q: draftQ }), 300)
    return () => window.clearTimeout(id)
  }, [draftQ, filter, writeFilter])

  const vm = useBuildProducts(filter)

  return (
    <div className="relative min-h-screen overflow-x-hidden">
      <Helmet>
        <title>{`Build · ${storeConfig.brand.name}`}</title>
      </Helmet>
      <div className="fixed inset-0 z-0">
        <StorefrontBackdrop />
      </div>
      <StorefrontNav
        onProfile={() => profile.open()}
        searchQuery={draftQ}
        onSearchChange={setDraftQ}
        onFilterToggle={() => setFilterOpen((v) => !v)}
        fullWidth
        showCart
        showBuild
      />
      <main className="relative z-10 px-6 pb-16 pt-28 sm:px-8 sm:pt-32">
        <section className="space-y-6">
          {vm.error !== null ? (
            <Alert tone="error">
              {vm.error}{' '}
              <button
                type="button"
                className="underline underline-offset-2 hover:text-charcoal"
                onClick={vm.reload}
              >
                Retry
              </button>
            </Alert>
          ) : null}

          {vm.isLoading && vm.items.length === 0 ? (
            <div className="flex items-center justify-center py-24">
              <Spinner />
            </div>
          ) : vm.items.length === 0 ? (
            <Placeholder>
              No products match your filters. Adjust the filters or clear the search.
            </Placeholder>
          ) : (
            <>
              <Stagger as="ul" className="flex flex-wrap gap-4" amount={0.05}>
                {vm.items.map((p) => (
                  <StaggerItem as="li" key={p.id} className="w-full sm:w-64">
                    <BuildProductCard product={p} />
                  </StaggerItem>
                ))}
              </Stagger>
              <Reveal>
                <Paginator
                  page={vm.page}
                  pageSize={vm.pageSize}
                  total={vm.total}
                  onPageChange={vm.setPage}
                  disabled={vm.isLoading}
                />
              </Reveal>
            </>
          )}
        </section>
      </main>

      <AnimatePresence>
        {filterOpen ? (
          <BuildFilterDrawer
            filter={filter}
            onFiltersChange={writeFilter}
            onClose={() => setFilterOpen(false)}
          />
        ) : null}
      </AnimatePresence>
    </div>
  )
}

function Placeholder({ children }: { readonly children: React.ReactNode }): JSX.Element {
  return (
    <div className="rounded-2xl border border-border/40 bg-bg-primary/72 px-6 py-12 text-center shadow-sm backdrop-blur-md">
      <p className="font-body text-body text-text-secondary">{children}</p>
    </div>
  )
}
